import Base from '../../Base/Base';
import { useContext, useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import { Button } from '@upmc/ims.component-library';
import StyleAuxiliary from '../../../hoc/Auxiliary/StyleAuxiliary/StyleAuxiliary';
import { Subtitle2, Headline5 } from '../../../components/UI/MDC/Typography/Typography';
import PersonSearch from '../../../components/UI/Input/PersonSearch/PersonSearch';
import { fetchPersonSearch } from './AIException_api';
import { nanoid } from 'nanoid';

import { createTheme, ThemeProvider } from '@mui/material/styles';
import { UserContext } from 'context/User/UserContext';
import Radio from '../../../components/UI/MDC/Radio/Radio';
import AIExceptionForm from './AIExceptionForm';
import MaterialIcon from '@material/react-material-icon';

import {
	indexRows,
	init,
	setRows,
	sortByHeader,
	toggleRow,
} from '../../../store/adminSearch/actions';
import { useDispatch, useSelector } from 'react-redux';

import { ACTION_TYPES } from '../../../components/UI/MDC/DataTable/_types';
import { ADMIN_HEADERS } from '../GlobalProtectExemption/_constants';

const theme = createTheme({
	palette: {
		primary: {
			main: '#771b61',
		},
	},
});

/**
 * Wrapping component for AI Exception
 *
 * @returns rendered component.
 */

const AIException = () => {
	const [formBody, setFormBody] = useState({
		Name: '',
		NTID: '',
		Esignon: '',
		Email: '',
		Department: '',
		Manager: '',
		Requestee_ESOFIID: '',
		Requestor_ESOFIID: '',
		GenAIName: '',
		Description: '',
	});

	/**
	 * @type {import('context/User/UserContext').ContextUser}
	 */
	const contextUser = useContext(UserContext);
	const user =
		contextUser.userInfo.impersonationProfile ??
		contextUser.userInfo.userProfile;
	const dispatch = useDispatch();
	const [step, setStep] = useState(0);
	const [myself, setMyself] = useState(null);
	const [trackingNumber, setTrackingNumber] = useState(null);
	const [errorMessage, setErrorMessage] = useState('');
	const [loading, setLoading] = useState(false);
	const rActions = useSelector(state => state.adminSearchTable.actions);
	const rHeaders = useSelector(state => state.adminSearchTable.headers);
	const rRows = useSelector(state => state.adminSearchTable.rows);

	// Manager roles which have access to submit exception for another user
	const MANAGER_ROLES = [
		{ ID: 13, F_FULL: 'UPMC Manager' },
		{ ID: 15, F_FULL: 'Account Requester' },
		{ ID: 18, F_FULL: 'DEV Team' },
		{ ID: 281, F_FULL: 'UPMC NonEmployee Manager' },
		{ ID: 830, F_FULL: 'UPMC Supervisor' },
	];

	// Check to see if user is a supervisor or manager
	const isSupervisor = () => {
		if (user?.Roles && Array.isArray(user.Roles)) {
			const roleIDs = user.Roles.map(role => role.ID);
			return roleIDs.some(id =>
				MANAGER_ROLES.some(role => role.ID === id)
			);
		}

		return false;
	};

	// Increment to next step after form body is generated
	useEffect(() => {
		if (formBody?.Name !== '') {
			setStep(prevStep => prevStep + 1);
		}
	}, [formBody]);

	// Initial redux store initialization for person search component
	useEffect(() => {
		// Generate the form body from the selected row
		const GenerateFormBody = row => {
			setFormBody(prevFormBody => ({
				...prevFormBody,
				Name: row?.Cells?.Name?.Value,
				NTID: row?.Cells?.Network_ID?.Value,
				Esignon: row?.Cells?.['UPMC_E-Sign-On_ID']?.Value,
				Requestee_ESOFIID: Number(row?.Cells?.FI_ID?.Value),
				Requestor_ESOFIID: user?.ID,
				Email: row?.Cells?.Email?.Value,
				Department: row?.Cells?.Department?.Value,
				Manager: row?.Cells?.Manager?.Value,
			}));
		};

		dispatch(
			init({
				actions: [
					{
						Type: ACTION_TYPES.TOGGLE,
						onChangedHandler: (_, __, row) => {
							GenerateFormBody(row);
							dispatch(toggleRow(row));
							dispatch(setRows([]));
						},
					},
				],
				headers: ADMIN_HEADERS.map((header, index) => {
					return {
						Value: header,
						props: {
							onSortChange: sortOrder =>
								dispatch(sortByHeader(header, sortOrder)),
							sort: index === 0 ? 1 : null,
						},
					};
				}),
			})
		);
	}, [dispatch, user.ID]);

	// Render different components based on step
	const RenderBody = () => {
		switch (step) {
			case 0:
				return (
					<>
						{isSupervisor ? (
							<>
								<Grid item>
									<Headline5>
										Do you want to submit an exception for
										yourself or for another user?
									</Headline5>
								</Grid>
								<Grid item>
									<Radio
										onChange={e => {
											setMyself(true);
											setStep(step + 1);
										}}
										checked={myself !== null && myself}
										label='Myself'
										name='Myself'
									/>
									<Radio
										onChange={e => {
											setMyself(false);
											setStep(step + 1);
										}}
										checked={myself !== null && !myself}
										label='Other'
										name='Other'
									/>
								</Grid>
							</>
						) : (
							(() => {
								setMyself(true);
								setStep(1);
							})()
						)}
					</>
				);
			case 1:
				if (myself) {
					return (
						<AIExceptionForm
							setStep={() => setStep(3)}
							{...{ setTrackingNumber }}
						/>
					);
				} else {
					return (
						<PersonSearch
							errorMessage={errorMessage}
							label='Person Search'
							dataTable={{
								label: 'Search Results',
								actions: rActions,
								headers: rHeaders,
								rows: rRows.filter(
									row => row.isDisplayed === true
								),
								onTextFieldChangedHandler: e =>
									dispatch(indexRows(e.target.value)),
							}}
							isLoading={loading}
							onButtonClickedHandler={payload => {
								setLoading(true);
								fetchPersonSearch(payload).then(response => {
									switch (response.status) {
										case 200:
											switch (
												response.data.responseCode
											) {
												case 200:
													let payload =
														response.data.payload;

													payload.forEach(row => {
														row.ActionCells = [
															{
																BindTo: 0,
																Checked: false,
																Key: nanoid(),
																Text: 'Select User',
															},
														];
														row.isDisplayed = true;
														return row;
													});
													dispatch(setRows(payload));
													setErrorMessage(null);
													setLoading(false);
													break;
												default:
													setErrorMessage(
														response.data
															.messages[0].Value
													);
													setLoading(false);
													break;
											}
											break;
										default:
											break;
									}
								});
							}}
							requestTarget='REQUESTEE'
						/>
					);
				}
			case 2:
				return (
					<AIExceptionForm
						userSelected={formBody}
						setStep={() => setStep(step + 1)}
						{...{ setTrackingNumber }}
					/>
				);
			case 3:
				return (
					<>
						<Subtitle2 align='center'>
							Thank you for submitting your AI 
							Exception Request!
							<br/>
							<i>Please allow up to 3 business days for review.</i>
						</Subtitle2>
						{trackingNumber && (
							<Subtitle2>
								<b>Tracking Number: </b>
								{trackingNumber}
							</Subtitle2>
						)}
						<Button
							variant='outlined'
							startIcon={<MaterialIcon icon='refresh' />}
							onClick={() => {
								setMyself(null);
								setStep(0);
							}}
							sx={{ margin: '5px' }}
							id='CreateAnotherRequest'
						>
							Start Another Request
						</Button>
					</>
				);
			default:
				return (
					<>
						<Grid item>
							<Headline5>
								Do you want to submit an exception for yourself
								or for another user?
							</Headline5>
						</Grid>
						<Grid item>
							<Radio
								onChange={e => {
									setMyself(true);
									setStep(step + 1);
								}}
								checked={myself !== null && myself}
								label='Myself'
								name='Myself'
							/>
							<Radio
								onChange={e => {
									setMyself(false);
									setStep(step + 1);
								}}
								checked={myself !== null && !myself}
								label='Other'
								name='Other'
							/>
						</Grid>
					</>
				);
		}
	};

	return (
		<Base title={'Unsanctioned AI Exception'}>
			<StyleAuxiliary>
				<ThemeProvider theme={theme}>
					<Grid
						container
						direction='column'
						spacing={1}
						sx={{
							alignItems: 'center',
							justifyContent: 'center',
						}}
					>
						{RenderBody()}
					</Grid>
				</ThemeProvider>
			</StyleAuxiliary>
		</Base>
	);
};

export default AIException;
